import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, Phone } from 'lucide-react';
import { Button } from './ui/button';
import CartButton from './CartButton';

const WHATSAPP_NUMBER = '551151250926';
const WHATSAPP_MESSAGE = 'Olá, vim pelo site de vocês. Quero fazer um orçamento para....';
const LOGO_URL = '/images/logo.png';

export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  
  // Verifica se estamos na página inicial (com hero escuro)
  const isHomePage = location.pathname === '/';
  // O header deve ter fundo claro se não estiver na home OU se scrollou
  const shouldHaveLightBg = !isHomePage || isScrolled;

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  const navLinks = [
    { href: '/#servicos', label: 'Serviços', isAnchor: true, anchor: 'servicos' },
    { href: '/#produtos', label: 'Produtos', isAnchor: true, anchor: 'produtos' },
    { href: '/conheca', label: 'Conheça', isAnchor: false },
    { href: '/produtos', label: 'Loja', isAnchor: false },
  ];

  const handleNavClick = (e, link) => {
    if (link.isAnchor) {
      e.preventDefault();
      
      // Usa navegação nativa com hash
      window.location.href = '/#' + link.anchor;
      
      // Fechar menu mobile se estiver aberto
      setIsMobileMenuOpen(false);
    }
  };

  const handleWhatsAppClick = () => {
    window.open(
      `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(WHATSAPP_MESSAGE)}`,
      '_blank'
    );
  };

  return (
    <header
      data-testid="header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        shouldHaveLightBg
          ? 'bg-white/95 backdrop-blur-md shadow-sm'
          : 'bg-transparent'
      }`}
    >
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        <nav className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link
            to="/"
            data-testid="logo-link"
            className="flex items-center gap-2 bg-transparent"
            onClick={() => {
              // Se já estamos na home, scroll para o topo
              if (isHomePage) {
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }
            }}
          >
            <img 
              src={LOGO_URL} 
              alt="Ematech Industrial" 
              className="h-9 md:h-11 w-auto object-contain bg-transparent"
              style={{ background: 'transparent' }}
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              link.isAnchor ? (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={(e) => handleNavClick(e, link)}
                  data-testid={`nav-${link.label.toLowerCase()}`}
                  className={`text-sm font-medium transition-colors hover:text-amber-500 cursor-pointer ${
                    shouldHaveLightBg ? 'text-slate-600' : 'text-white/90'
                  }`}
                >
                  {link.label}
                </a>
              ) : (
                <Link
                  key={link.href}
                  to={link.href}
                  data-testid={`nav-${link.label.toLowerCase()}`}
                  className={`text-sm font-medium transition-colors hover:text-amber-500 ${
                    shouldHaveLightBg ? 'text-slate-600' : 'text-white/90'
                  }`}
                >
                  {link.label}
                </Link>
              )
            ))}
          </div>

          {/* CTA Button + Cart */}
          <div className="hidden md:flex items-center gap-3">
            <CartButton />
            <Button
              data-testid="header-whatsapp-btn"
              onClick={handleWhatsAppClick}
              className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold px-5 py-2 rounded-lg flex items-center gap-2 shadow-sm hover:shadow transition-all duration-200"
            >
              <Phone className="w-4 h-4" />
              Orçamento
            </Button>
          </div>

          {/* Mobile Cart + Menu Button */}
          <div className="md:hidden flex items-center gap-2">
            <CartButton />
            <button
              data-testid="mobile-menu-btn"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className={`p-2 rounded-md transition-colors ${
                shouldHaveLightBg ? 'text-slate-900' : 'text-white'
              }`}
              aria-label="Toggle menu"
            >
              {isMobileMenuOpen ? (
                <X className="w-6 h-6" />
              ) : (
                <Menu className="w-6 h-6" />
              )}
            </button>
          </div>
        </nav>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div
            data-testid="mobile-menu"
            className="md:hidden bg-white border-t border-slate-100 shadow-lg"
          >
            <div className="py-4 px-4 space-y-3">
              {navLinks.map((link) => (
                link.isAnchor ? (
                  <a
                    key={link.href}
                    href={link.href}
                    onClick={(e) => handleNavClick(e, link)}
                    data-testid={`mobile-nav-${link.label.toLowerCase()}`}
                    className="block py-2 text-slate-700 font-medium hover:text-amber-500 transition-colors cursor-pointer"
                  >
                    {link.label}
                  </a>
                ) : (
                  <Link
                    key={link.href}
                    to={link.href}
                    data-testid={`mobile-nav-${link.label.toLowerCase()}`}
                    className="block py-2 text-slate-700 font-medium hover:text-amber-500 transition-colors"
                  >
                    {link.label}
                  </Link>
                )
              ))}
              <Button
                data-testid="mobile-whatsapp-btn"
                onClick={handleWhatsAppClick}
                className="w-full bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold py-3 rounded-lg flex items-center justify-center gap-2 mt-4"
              >
                <Phone className="w-4 h-4" />
                Falar no WhatsApp
              </Button>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
